// V96-0-3: one canonical framework id space.
//
// The database keys frameworks with hyphenated ids (`nist-ai-rmf`,
// `soc2-ai`); code grew underscore spellings (`nist_ai_rmf`, `soc2_ai`,
// `nist_800_53`) that only one call site translated. A mismatch doesn't
// throw — it silently drops a framework from readiness. Every framework id
// that crosses a boundary (profile JSON, control refs, posture lists) goes
// through `canonicalFrameworkId`, and code constants use FRAMEWORK_IDS.
//
// Pure data — no runtime imports.
export const FRAMEWORK_IDS = {
    NIST_AI_RMF: 'nist-ai-rmf',
    NIST_AI_600_1: 'nist-ai-600-1',
    NIST_AGENTIC: 'nist-agentic',
    ISO_42001: 'iso-42001',
    EU_AI_ACT: 'eu-ai-act',
    SOC2: 'soc2',
    SOC2_AI: 'soc2-ai',
    FEDRAMP_REV5: 'fedramp-rev5',
    FEDRAMP_CR26: 'fedramp-cr26',
    FEDRAMP_20X_PILOT: 'fedramp-20x-pilot',
    FEDRAMP_AI_OVERLAY: 'fedramp-ai-overlay',
    NIST_800_53: 'nist-800-53',
    CISA_AI_SBOM: 'cisa-ai-sbom',
    HIPAA: 'hipaa',
    HITRUST: 'hitrust-csf-v11',
    FDA_GMLP: 'fda-gmlp',
    HHS_OCR_1557: 'hhs-ocr-1557',
    CFR_42_PART_2: '42-cfr-part-2',
    GDPR: 'gdpr',
    UK_GDPR: 'uk-gdpr',
    CMMC_L1: 'cmmc-l1',
    CMMC_L2: 'cmmc-l2',
    CMMC_L3: 'cmmc-l3',
    // V111-6-1 (D-111-9): crosswalk-only AI threat vocabularies — controls
    // reference their threat/technique ids via control_edges; nothing is
    // assessed against them and edges never credit readiness.
    OWASP_LLM_TOP10: 'owasp-llm-top10',
    OWASP_AGENTIC_TOP10: 'owasp-agentic-top10',
    MITRE_ATLAS: 'mitre-atlas',
};
export const CANONICAL_FRAMEWORK_IDS = new Set(Object.values(FRAMEWORK_IDS));
/**
 * Legacy spellings seen in code, persisted profile JSON, and posture
 * lists. Only entries whose meaning differs from a plain `_`→`-` swap
 * need to be listed; the swap covers the rest.
 */
const LEGACY_ALIASES = {
    fedramp: FRAMEWORK_IDS.FEDRAMP_REV5,
    fedramp_20x: FRAMEWORK_IDS.FEDRAMP_CR26,
    'fedramp-20x': FRAMEWORK_IDS.FEDRAMP_CR26,
    hipaa_ai: FRAMEWORK_IDS.HIPAA,
    hitrust: FRAMEWORK_IDS.HITRUST,
    'hitrust-csf': FRAMEWORK_IDS.HITRUST,
};
/**
 * Canonical id for any known spelling. Unknown ids come back lower-cased
 * with underscores swapped for hyphens, so callers can still compare two
 * unknown ids consistently; use `isKnownFrameworkId` to reject them.
 */
export function canonicalFrameworkId(id) {
    const lowered = id.trim().toLowerCase();
    const aliased = LEGACY_ALIASES[lowered];
    if (aliased)
        return aliased;
    return lowered.replace(/_/g, '-');
}
export function isKnownFrameworkId(id) {
    return CANONICAL_FRAMEWORK_IDS.has(canonicalFrameworkId(id));
}
export function sameFramework(a, b) {
    return canonicalFrameworkId(a) === canonicalFrameworkId(b);
}
/** Canonicalize and de-duplicate a list, preserving first-seen order. */
export function canonicalFrameworkList(ids) {
    return [...new Set(ids.map(canonicalFrameworkId))];
}
//# sourceMappingURL=framework-ids.js.map